/**
 * Badge Hooks
 */
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { badgeService } from '../services/badgeService';
import { queryTags } from '../queryTags';
import { getQueryClient } from '../queryClient';
import type {
  CreateBadgePayload,
  UpdateBadgePayload,
  GetBadgesParams,
} from '../types/badge.types';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';

/**
 * Get badges hook
 */
export function useGetBadges(
  params?: GetBadgesParams,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [
      queryTags.badge || 'badge',
      params?.search || '',
      params?.page || 1,
      params?.limit || 10,
    ],
    queryFn: () => badgeService.getBadges(params),
    ...options,
  });
}

/**
 * Get single badge hook
 */
export function useGetBadge(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.badge || 'badge', id],
    queryFn: () => badgeService.getBadge(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Create badge mutation hook
 */
export function useCreateBadge(
  options?: UseMutationOptions<any, any, CreateBadgePayload, any>
) {
  return useApiMutation<any, any, CreateBadgePayload, any>({
    mutationFn: ((data: CreateBadgePayload) => badgeService.createBadge(data)) as any,
    onSuccess: (data, variables, context, mutation) => {
      // Always invalidate queries to refresh the list
      getQueryClient().invalidateQueries({ queryKey: [queryTags.badge || 'badge'] });
      // Call custom onSuccess if provided
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, mutation);
      }
    },
    onError: options?.onError,
  });
}

/**
 * Update badge mutation hook
 */
export function useUpdateBadge(
  options?: UseMutationOptions<any, any, { id: string; data: UpdateBadgePayload }, any>
) {
  return useApiMutation<any, any, { id: string; data: UpdateBadgePayload }, any>({
    mutationFn: ((variables: { id: string; data: UpdateBadgePayload }) =>
      badgeService.updateBadge(variables.id, variables.data)) as any,
    onSuccess: (data, variables, context, mutation) => {
      // Always invalidate queries to refresh the list
      getQueryClient().invalidateQueries({ queryKey: [queryTags.badge || 'badge'] });
      // Call custom onSuccess if provided
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, mutation);
      }
    },
    onError: options?.onError,
  });
}

/**
 * Delete badge mutation hook
 */
export function useDeleteBadge(
  options?: UseMutationOptions<any, any, string, any>
) {
  return useApiMutation<any, any, string, any>({
    mutationFn: ((id: string) => badgeService.deleteBadge(id)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.badge || 'badge'] });
    },
    ...options,
  });
}

